import { LOCALES, type Locale } from '@link/share/i18n';

/**
 * Build the full set of SEO tags for a route, as react-router `meta` descriptors.
 *
 * Replaces the SPA's `useSEO()` effect. That hook could only mutate `document.head`
 * after the bundle executed, so a crawler that does not run JavaScript saw only
 * the static tags in `index.html` — the same title and description on every page,
 * in one language. Rendering the tags on the server is the point of the migration,
 * so this is deliberately a superset of what the hook produced rather than the
 * four tags the first SSR route shipped with.
 *
 * Everything is derived from arguments; nothing here reads a browser global or a
 * module singleton, so it is safe to call during a server render.
 */

/**
 * Maps our locale codes to the BCP 47 tags `hreflang` requires.
 *
 * `zh`/`zt` are ours alone: the spec has no such tags, and a crawler reading
 * `hreflang="zt"` treats it as an unknown language and drops the annotation.
 * Also used by `useSEO`, so the SPA and server-rendered pages emit the same tags.
 */
export const HREFLANG_MAP: Record<Locale, string> = {
  en: 'en',
  ja: 'ja',
  zh: 'zh-Hans',
  zt: 'zh-Hant',
  ko: 'ko'
};

export interface SeoMetaOptions {
  /**
   * The request URL, as `request.url` inside a loader.
   *
   * The server has no `window.location.origin`, and hard-coding the origin would
   * need one value per environment (test, staging, production) with nothing to
   * catch a mismatch. Taking it from the request is correct in every environment
   * for free, including preview deploys on generated hostnames.
   */
  url: string;
  /** Locale this render is in; sets `og:locale` and marks the canonical row. */
  locale: Locale;
  /** Page title, before the site name is appended. */
  title: string;
  description: string;
  /** Site name, appended to the title and sent as `og:site_name`. */
  siteName: string;
  /** Absolute URL of the sharing image. Omitted entirely when absent. */
  ogImage?: string;
  ogType?: string;
  /**
   * Locales that have a version of *this* page, for the `alternate` rows.
   *
   * Defaults to every supported locale, which is right for pages that exist in
   * all of them. Pages that do not must narrow it: a product translated into two
   * languages, or the Shinkansen flow, which has no Japanese. Advertising an
   * alternate that 404s or silently falls back is worse than omitting it, since
   * it invites crawlers to index duplicates of the fallback language.
   */
  hreflangs?: readonly Locale[];
  /**
   * Locale the `x-default` row points at — what a crawler should serve when none
   * of the visitor's languages match.
   *
   * Passed explicitly rather than taken from `hreflangs[0]`, which would make the
   * site-wide default an artefact of menu order: reordering the language picker
   * would silently repoint `x-default`. Must be a member of `hreflangs`, or the
   * row advertises a version this page does not have.
   */
  defaultLocale: Locale;
  /**
   * Query parameters that identify *distinct content* and so belong in the
   * canonical URL.
   *
   * Everything else is dropped, collapsing tracking and UI-state parameters onto
   * one canonical URL instead of letting each variant compete as its own page.
   * Replaces the SPA's `handle.canonicalQueryKeys`, which `useSEO` read off the
   * route match — a route module can pass it directly here.
   */
  canonicalQueryKeys?: readonly string[];
  /** Defaults to `index, follow`; pass `noindex, nofollow` to withhold a page. */
  robots?: string;
}

/** A react-router meta descriptor. */
type MetaDescriptor =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { tagName: 'link'; rel: string; href: string; hrefLang?: string };

/** Replace the leading `/:lang` segment, keeping the rest of the path. */
function swapLocale(pathname: string, locale: Locale): string {
  const segments = pathname.split('/');
  // ['', 'ja', 'product', ...] — index 1 is the lang segment. A path without one
  // (only ever '/', which redirects) is left alone rather than corrupted.
  if (segments.length > 1 && segments[1]) {
    segments[1] = locale;
    return segments.join('/');
  }
  return `/${locale}`;
}

/**
 * Absolute URL for `pathname`, carrying over only the whitelisted query params.
 *
 * Absolute because `canonical` and `og:url` are meaningless as relative paths —
 * a crawler resolves them against the document, which defeats the purpose of
 * declaring them.
 */
function buildAbsolute(
  origin: string,
  pathname: string,
  search: URLSearchParams,
  canonicalQueryKeys: readonly string[]
): string {
  const kept = new URLSearchParams();
  canonicalQueryKeys.forEach(key => {
    const value = search.get(key);
    if (value) {
      kept.set(key, value);
    }
  });
  const query = kept.toString();
  return `${origin}${pathname}${query ? `?${query}` : ''}`;
}

export function buildSeoMeta({
  url,
  locale,
  title,
  description,
  siteName,
  ogImage,
  ogType = 'website',
  hreflangs = LOCALES,
  defaultLocale,
  canonicalQueryKeys = [],
  robots = 'index, follow'
}: SeoMetaOptions): MetaDescriptor[] {
  const { origin, pathname: requestPath, searchParams } = new URL(url);

  // On a client-side navigation the router fetches loader data from
  // `<path>.data` — `/_root.data` for `/` — and that is the `request.url` the
  // loader sees and passes here. Left as-is, every page reached by clicking a
  // link would declare `…/product/123.data` as its canonical and og:url. Only
  // the document request carries the real path, so map the data URL back to
  // the page it belongs to. (Its `_routes` query param needs no handling: the
  // query is rebuilt from `canonicalQueryKeys` below.)
  const pathname =
    requestPath === '/_root.data' ? '/' : requestPath.replace(/\.data$/, '');

  // Canonicalise the `:lang` segment to the locale that was actually rendered,
  // rather than echoing back whatever the request asked for. An unsupported
  // segment falls back (`/fr` renders English), and a self-referencing canonical
  // on `/fr` would nominate a non-existent locale URL as the indexable version
  // of the English page — the duplicate-content problem canonical exists to
  // prevent. It also keeps the canonical inside its own hreflang set, which
  // search engines require; the alternates below are already locale-swapped.
  const canonicalPath = swapLocale(pathname, locale);

  const canonical = buildAbsolute(
    origin,
    canonicalPath,
    searchParams,
    canonicalQueryKeys
  );

  // "<page> - <site>", matching the SPA's `document.title` composition. A page
  // whose title is already the site name is left alone rather than doubled.
  const fullTitle = title === siteName ? title : `${title} - ${siteName}`;

  const tags: MetaDescriptor[] = [
    { title: fullTitle },
    { name: 'description', content: description },
    { name: 'robots', content: robots },

    { property: 'og:type', content: ogType },
    { property: 'og:site_name', content: siteName },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonical },
    { property: 'og:locale', content: HREFLANG_MAP[locale] },

    { tagName: 'link', rel: 'canonical', href: canonical }
  ];

  if (ogImage) {
    tags.push({ property: 'og:image', content: ogImage });
    // Without this, X/Twitter renders a link with no preview even though the
    // Open Graph tags are present.
    tags.push({ name: 'twitter:card', content: 'summary_large_image' });
  }

  // One `alternate` row per translated version, plus `x-default` for crawlers
  // choosing when none of the visitor's languages match. Every row — including
  // the current locale's — is required: hreflang sets must be self-referencing,
  // or search engines discard the whole group.
  hreflangs.forEach(alternate => {
    tags.push({
      tagName: 'link',
      rel: 'alternate',
      hrefLang: HREFLANG_MAP[alternate],
      href: buildAbsolute(
        origin,
        swapLocale(pathname, alternate),
        searchParams,
        canonicalQueryKeys
      )
    });
  });

  // Only when the default is actually one of this page's languages — pointing
  // x-default at a version that does not exist is worse than omitting the row.
  if (hreflangs.includes(defaultLocale)) {
    tags.push({
      tagName: 'link',
      rel: 'alternate',
      hrefLang: 'x-default',
      href: buildAbsolute(
        origin,
        swapLocale(pathname, defaultLocale),
        searchParams,
        canonicalQueryKeys
      )
    });
  }

  return tags;
}
